/* do-cart.js - cart and pre-checkout behaviour (v23 lane, batch 5)

   1. SERIALIZATION, NOT DEBOUNCE. Every mutation runs through one promise
      chain, so two writes are never in flight against the same line.
      Debounce drops intent; a queue honours it.
   2. OPTIMISTIC, THEN TRUE. The input flips at once; the response is the
      arbiter. On failure the pre-write snapshot is restored.
   3. NO INVENTED NUMBERS. Never formats money, never guesses an available
      count. Prices come back from Liquid via the Section Rendering API; an
      inventory ceiling is read from the cart Shopify returns after it
      refused the write. Otherwise the page reloads.
   4. ENHANCEMENT ONLY. With JS off the cart form posts, the discount field
      submits ?discount= to the cart URL, gift attributes post with the cart
      form. Only save-for-later, which has no backend, disappears - and its
      markup says so.

   Markup contract (data attributes, all optional):
     [data-do-cart] root, data-do-sections="id,id"
     [data-do-cart-line] data-do-line-key, [data-do-qty-input],
     [data-do-qty-plus], [data-do-qty-minus], [data-do-line-remove],
     [data-do-save-later], [data-do-section-id]
*/
(function () {
  'use strict';

  var doc = document;
  doc.documentElement.classList.add('do-js');

  var base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  if (base.charAt(base.length - 1) !== '/') { base += '/'; }
  var CART = base + 'cart';
  var SAVED_KEY = 'do:saved:v1';

  /* ---- announcements -------------------------------------------- */
  var live = doc.querySelector('[data-do-live]');
  if (!live) {
    live = doc.createElement('p');
    live.className = 'do-sr';
    live.setAttribute('data-do-live', '');
    live.setAttribute('role', 'status');
    live.setAttribute('aria-live', 'polite');
    live.setAttribute('aria-atomic', 'true');
    doc.body.appendChild(live);
  }
  function say(msg) { live.textContent = ''; window.setTimeout(function () { live.textContent = msg; }, 60); }

  /* ---- request queue --------------------------------------------
     One chain. Each job waits for the previous one to settle, pass or
     fail, so cart state cannot interleave. */
  var chain = Promise.resolve();
  function enqueue(job) {
    var out = chain.then(job, job);
    chain = out.then(function () {}, function () {});
    return out;
  }

  function post(path, body) {
    return fetch(path, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (res) {
      return res.json().catch(function () { return {}; }).then(function (data) {
        if (!res.ok) { var e = new Error('cart'); e.status = res.status; e.data = data; throw e; }
        return data;
      });
    }, function () { var e = new Error('network'); e.network = true; throw e; });
  }

  function getCart() {
    return fetch(base + 'cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); });
  }

  /* ---- section rendering ----------------------------------------
     The only way money gets on screen: Liquid renders it, we swap it in. */
  function sectionIds() {
    var host = doc.querySelector('[data-do-cart]');
    var ids = host && host.getAttribute('data-do-sections');
    return ids ? ids.split(',') : [];
  }

  function refresh() {
    var ids = sectionIds();
    if (!ids.length) { window.location.reload(); return Promise.resolve(); }
    return fetch(CART + '?sections=' + encodeURIComponent(ids.join(',')), {
      credentials: 'same-origin', headers: { Accept: 'application/json' }
    }).then(function (r) { return r.json(); }).then(function (data) {
      ids.forEach(function (id) {
        var target = doc.querySelector('[data-do-section-id="' + id + '"]');
        if (!target || !data[id]) { return; }
        var parsed = new DOMParser().parseFromString(data[id], 'text/html');
        var fresh = parsed.querySelector('[data-do-section-id="' + id + '"]');
        target.innerHTML = fresh ? fresh.innerHTML : data[id];
      });
      bindSaved();
      renderSaved();
    });
  }

  function busy(on) {
    var nodes = doc.querySelectorAll('[data-do-summary-subtotal], [data-do-summary-total], [data-do-line-price]');
    for (var i = 0; i < nodes.length; i++) {
      nodes[i].setAttribute('data-do-state', on ? 'loading' : 'default');
    }
  }

  /* ---- error surface --------------------------------------------
     Markup and wording live in snippets/do-cart-errors.liquid. This only
     picks a template and fills the two spans it owns. */
  function clearErrors() {
    var box = doc.querySelector('[data-do-cart-errors]');
    if (box) { box.innerHTML = ''; box.setAttribute('data-do-state', 'empty'); }
  }

  function showError(key, opts) {
    opts = opts || {};
    var box = doc.querySelector('[data-do-cart-errors]');
    var tpl = doc.querySelector('[data-do-error-template="' + key + '"]');
    if (!box || !tpl) { say('That cart update did not go through. Nothing was changed.'); return; }
    box.innerHTML = '';
    box.removeAttribute('data-do-state');
    var node = tpl.content.cloneNode(true);
    var t = node.querySelector('[data-do-error-title]');
    if (t && opts.title) { t.textContent = opts.title; }
    var a = node.querySelector('[data-do-error-available]');
    if (a && typeof opts.available === 'number') { a.textContent = String(opts.available); }
    var remove = node.querySelector('[data-do-error-remove]');
    if (remove && opts.key) { remove.setAttribute('data-do-line-key', opts.key); }
    var retry = node.querySelector('[data-do-error-retry]');
    if (retry && opts.retry) { retry.addEventListener('click', opts.retry); }
    box.appendChild(node);
    var head = box.querySelector('.do-cart-error__head');
    if (head) { say(head.textContent); }
  }

  doc.addEventListener('click', function (e) {
    var d = e.target.closest('[data-do-error-dismiss]');
    if (d) { clearErrors(); return; }
    var r = e.target.closest('[data-do-error-remove]');
    if (r) { changeLine(r.getAttribute('data-do-line-key'), 0, null); }
  });

  /* ---- quantity: optimistic write, rollback on refusal ----------- */
  function lineOf(el) { return el.closest('[data-do-cart-line]'); }

  function changeLine(key, qty, input) {
    if (!key) { return Promise.resolve(); }
    var before = input ? input.value : null;
    var line = input ? lineOf(input) : null;
    var title = line ? (line.getAttribute('data-do-line-title') || '') : '';
    if (input) { input.value = String(qty); }
    clearErrors();
    busy(true);
    return enqueue(function () {
      return post(base + 'cart/change.js', { id: key, quantity: qty })
        .then(function () {
          return refresh().then(function () {
            busy(false);
            say(qty === 0 ? 'Line removed. Cart updated.' : 'Cart updated.');
          });
        })
        .catch(function (err) {
          busy(false);
          if (err.network) {
            if (input && before !== null) { input.value = before; }
            showError('network', { retry: function () { changeLine(key, qty, input); } });
            return;
          }
          return getCart().then(function (cart) {
            var found = null;
            (cart.items || []).forEach(function (it) { if (it.key === key) { found = it; } });
            if (!found) {
              if (input && before !== null) { input.value = before; }
              showError('unavailable', { title: title, key: key });
              return;
            }
            if (input) { input.value = String(found.quantity); }
            if (found.quantity === 0) { showError('sold_out', { title: title || found.title, key: key }); }
            else { showError('inventory', { title: title || found.title, available: found.quantity, key: key }); }
          }).catch(function () {
            if (input && before !== null) { input.value = before; }
            showError('network', { retry: function () { changeLine(key, qty, input); } });
          });
        });
    });
  }

  function step(btn, delta) {
    var line = lineOf(btn);
    if (!line) { return; }
    var input = line.querySelector('[data-do-qty-input]');
    var key = line.getAttribute('data-do-line-key');
    var next = Math.max(0, (parseInt(input && input.value, 10) || 0) + delta);
    changeLine(key, next, input);
  }

  doc.addEventListener('click', function (e) {
    var plus = e.target.closest('[data-do-qty-plus]');
    if (plus) { e.preventDefault(); step(plus, 1); return; }
    var minus = e.target.closest('[data-do-qty-minus]');
    if (minus) { e.preventDefault(); step(minus, -1); return; }
    var rm = e.target.closest('[data-do-line-remove]');
    if (rm) {
      e.preventDefault();
      var l = lineOf(rm);
      if (l) { changeLine(l.getAttribute('data-do-line-key'), 0, l.querySelector('[data-do-qty-input]')); }
    }
  });

  doc.addEventListener('change', function (e) {
    var input = e.target.closest('[data-do-qty-input]');
    if (!input) { return; }
    var line = lineOf(input);
    if (!line) { return; }
    var q = Math.max(0, parseInt(input.value, 10) || 0);
    changeLine(line.getAttribute('data-do-line-key'), q, input);
  });

  /* ---- discount --------------------------------------------------
     A typed code is a request. Only cart.js decides what it is. */
  function applyDiscount(host) {
    var input = host.querySelector('[data-do-discount-input]');
    var status = host.querySelector('[data-do-discount-status]');
    var errBox = host.querySelector('[data-do-discount-error]');
    var submit = host.querySelector('[data-do-discount-submit]');
    var code = (input.value || '').trim();
    if (!code) { return; }

    function msg(name) { return (host.getAttribute('data-do-msg-' + name) || '').split('%CODE%').join(code); }
    function fail(name, state) {
      input.setAttribute('data-do-state', state);
      if (errBox) { errBox.hidden = false; errBox.textContent = msg(name); }
      if (status) { status.textContent = ''; }
    }

    if (errBox) { errBox.hidden = true; errBox.textContent = ''; }
    input.setAttribute('data-do-state', 'loading');
    input.setAttribute('aria-busy', 'true');
    if (submit) { submit.setAttribute('data-do-state', 'disabled'); submit.setAttribute('aria-disabled', 'true'); }
    if (status) { status.textContent = msg('validating'); }

    enqueue(function () {
      return getCart().then(function (before) {
        var had = (before.discount_codes || []).some(function (d) {
          return String(d.code).toLowerCase() === code.toLowerCase();
        });
        if (had) { fail('already', 'selected'); return null; }
        return fetch(CART + '?discount=' + encodeURIComponent(code), { credentials: 'same-origin' })
          .then(function () { return getCart(); })
          .then(function (after) {
            var entry = null;
            (after.discount_codes || []).forEach(function (d) {
              if (String(d.code).toLowerCase() === code.toLowerCase()) { entry = d; }
            });
            if (!entry) { fail('invalid', 'error'); return null; }
            var applications = after.cart_level_discount_applications || [];
            var lineDiscount = (after.items || []).some(function (it) {
              return (it.line_level_discount_allocations || []).length > 0;
            });
            if (!applications.length && !lineDiscount && !entry.amount) {
              fail('notapplicable', 'partial');
              return null;
            }
            input.setAttribute('data-do-state', 'success');
            input.value = '';
            if (status) { status.textContent = entry.code + ' applied.'; }
            say(entry.code + ' applied.');
            return refresh();
          });
      }).catch(function () {
        fail('network', 'error');
      }).then(function () {
        input.removeAttribute('aria-busy');
        if (submit) { submit.setAttribute('data-do-state', 'default'); submit.removeAttribute('aria-disabled'); }
      });
    });
  }

  doc.addEventListener('submit', function (e) {
    var form = e.target.closest('[data-do-discount-form]');
    if (!form) { return; }
    var host = form.closest('[data-do-discount]');
    if (!host) { return; }
    e.preventDefault();
    applyDiscount(host);
  });

  /* ---- gift options ---------------------------------------------- */
  function countNote(note) {
    var max = parseInt(note.getAttribute('data-do-gift-max'), 10) || 500;
    var left = max - (note.value || '').length;
    var out = doc.querySelector('[data-do-gift-count]');
    if (!out) { return; }
    out.setAttribute('aria-live', left <= 20 ? 'polite' : 'off');
    out.setAttribute('data-do-count', left === 0 ? 'full' : (left <= 20 ? 'near' : 'ok'));
    out.textContent = left + ' of ' + max + ' characters left';
  }

  doc.addEventListener('input', function (e) {
    var note = e.target.closest('[data-do-gift-note]');
    if (note) { countNote(note); }
  });

  doc.addEventListener('submit', function (e) {
    var form = e.target.closest('[data-do-gift-form]');
    if (!form) { return; }
    e.preventDefault();
    var status = form.querySelector('[data-do-gift-status]');
    var note = form.querySelector('[data-do-gift-note]');
    var receipt = form.querySelector('[data-do-gift-receipt]');
    var attrs = {};
    attrs['Gift note'] = note ? note.value : '';
    attrs['Gift receipt'] = receipt && receipt.checked ? 'Yes' : '';
    if (status) { status.textContent = 'Saving gift options.'; }
    enqueue(function () {
      return post(base + 'cart/update.js', { attributes: attrs }).then(function () {
        var jobs = [];
        var fields = form.querySelectorAll('[data-do-gift-recipient]');
        for (var i = 0; i < fields.length; i++) {
          (function (field) {
            var props = {};
            var email = (field.value || '').trim();
            props['Recipient email'] = email;
            props.__shopify_send_gift_card_to_recipient = email ? 'on' : '';
            jobs.push(post(base + 'cart/change.js', { id: field.getAttribute('data-do-line-key'), properties: props }));
          }(fields[i]));
        }
        return Promise.all(jobs);
      }).then(function () {
        if (status) { status.textContent = 'Gift options saved with this order.'; }
        say('Gift options saved with this order.');
        return refresh();
      }).catch(function () {
        if (status) { status.textContent = ''; }
        showError('network', { retry: function () { form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true })); } });
      });
    });
  });

  /* ---- save for later -------------------------------------------
     localStorage only. Device-local, not reserved; the snippet says so. */
  function readSaved() {
    try { return JSON.parse(window.localStorage.getItem(SAVED_KEY) || '[]'); } catch (err) { return []; }
  }
  function writeSaved(list) {
    try { window.localStorage.setItem(SAVED_KEY, JSON.stringify(list)); } catch (err) { /* private mode: list is simply not kept */ }
  }

  function renderSaved() {
    var host = doc.querySelector('[data-do-saved]');
    if (!host) { return; }
    var list = host.querySelector('[data-do-saved-list]');
    var empty = host.querySelector('[data-do-saved-empty]');
    var tpl = host.querySelector('[data-do-saved-template]');
    if (!list || !tpl) { return; }
    var items = readSaved();
    list.innerHTML = '';
    list.hidden = items.length === 0;
    if (empty) { empty.hidden = items.length > 0; }
    items.forEach(function (it) {
      var node = tpl.content.cloneNode(true);
      var el = node.querySelector('[data-do-saved-item]');
      el.setAttribute('data-do-variant', it.id);
      var link = node.querySelector('[data-do-saved-link]');
      if (link && it.url) { link.href = it.url; }
      var img = node.querySelector('[data-do-saved-img]');
      if (img) { if (it.image) { img.src = it.image; } else { img.remove(); } }
      node.querySelector('[data-do-saved-name]').textContent = it.title || 'Saved item';
      node.querySelector('[data-do-saved-meta]').textContent = it.variant || '';
      node.querySelector('[data-do-saved-price]').textContent = it.price || '';
      list.appendChild(node);
    });
  }

  function saveForLater(btn) {
    var line = lineOf(btn);
    if (!line) { return; }
    var key = line.getAttribute('data-do-line-key');
    var id = line.getAttribute('data-do-variant-id');
    var input = line.querySelector('[data-do-qty-input]');
    var qty = parseInt(input && input.value, 10) || 1;
    var priceEl = line.querySelector('[data-do-line-price]');
    var imgEl = line.querySelector('img');
    var linkEl = line.querySelector('a');
    var entry = {
      id: id,
      quantity: qty,
      title: line.getAttribute('data-do-line-title') || '',
      variant: line.getAttribute('data-do-line-variant') || '',
      price: priceEl ? priceEl.textContent.trim() : '',
      image: imgEl ? imgEl.getAttribute('src') : '',
      url: linkEl ? linkEl.getAttribute('href') : ''
    };
    changeLine(key, 0, input).then(function () {
      var list = readSaved();
      list.unshift(entry);
      writeSaved(list);
      renderSaved();
      say('Moved to saved for later. Saved items stay in this browser and are not reserved.');
    });
  }

  function moveToCart(btn) {
    var el = btn.closest('[data-do-saved-item]');
    var id = el && el.getAttribute('data-do-variant');
    if (!id) { return; }
    var entry = null;
    readSaved().forEach(function (it) { if (String(it.id) === String(id)) { entry = it; } });
    btn.setAttribute('data-do-state', 'loading');
    btn.setAttribute('aria-busy', 'true');
    enqueue(function () {
      return post(base + 'cart/add.js', { items: [{ id: Number(id), quantity: (entry && entry.quantity) || 1 }] })
        .then(function () {
          writeSaved(readSaved().filter(function (it) { return String(it.id) !== String(id); }));
          renderSaved();
          say('Moved back into your cart.');
          return refresh();
        })
        .catch(function (err) {
          btn.setAttribute('data-do-state', 'default');
          btn.removeAttribute('aria-busy');
          if (err.network) { showError('network', { retry: function () { moveToCart(btn); } }); }
          else { showError('sold_out', { title: (entry && entry.title) || 'That item' }); }
        });
    });
  }

  function bindSaved() { renderSaved(); }

  doc.addEventListener('click', function (e) {
    var save = e.target.closest('[data-do-save-later]');
    if (save) { e.preventDefault(); saveForLater(save); return; }
    var move = e.target.closest('[data-do-saved-move]');
    if (move) { e.preventDefault(); moveToCart(move); return; }
    var drop = e.target.closest('[data-do-saved-drop]');
    if (drop) {
      e.preventDefault();
      var el = drop.closest('[data-do-saved-item]');
      var id = el && el.getAttribute('data-do-variant');
      writeSaved(readSaved().filter(function (it) { return String(it.id) !== String(id); }));
      renderSaved();
      say('Removed from saved for later.');
    }
  });

  /* ---- drawer: focus in, focus back ------------------------------ */
  var lastFocus = null;

  function focusables(panel) {
    return Array.prototype.filter.call(
      panel.querySelectorAll('a[href], button:not([disabled]), input, select, textarea, [tabindex]'),
      function (el) { return el.offsetParent !== null && el.getAttribute('aria-disabled') !== 'true'; }
    );
  }

  function openDrawer(drawer) {
    if (!drawer) { return; }
    lastFocus = doc.activeElement;
    drawer.hidden = false;
    drawer.setAttribute('data-do-open', 'true');
    var panel = drawer.querySelector('.do-drawer__panel') || drawer;
    panel.setAttribute('tabindex', '-1');
    panel.focus();
    doc.documentElement.style.overflow = 'hidden';
  }

  function closeDrawer(drawer) {
    if (!drawer) { return; }
    drawer.setAttribute('data-do-open', 'false');
    doc.documentElement.style.overflow = '';
    window.setTimeout(function () { drawer.hidden = true; }, 220);
    if (lastFocus && lastFocus.focus) { lastFocus.focus(); }
  }

  doc.addEventListener('click', function (e) {
    var open = e.target.closest('[data-do-drawer-open]');
    if (open) { e.preventDefault(); openDrawer(doc.querySelector('[data-do-drawer]')); return; }
    var close = e.target.closest('[data-do-drawer-close], .do-drawer__scrim');
    if (close) { e.preventDefault(); closeDrawer(close.closest('[data-do-drawer]')); }
  });

  doc.addEventListener('keydown', function (e) {
    var drawer = doc.querySelector('[data-do-drawer][data-do-open="true"]');
    if (!drawer) { return; }
    if (e.key === 'Escape') { closeDrawer(drawer); return; }
    if (e.key !== 'Tab') { return; }
    var items = focusables(drawer);
    if (!items.length) { return; }
    var first = items[0];
    var last = items[items.length - 1];
    if (e.shiftKey && doc.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && doc.activeElement === last) { e.preventDefault(); first.focus(); }
  });

  /* ---- init ------------------------------------------------------ */
  function init() {
    renderSaved();
    var note = doc.querySelector('[data-do-gift-note]');
    if (note) { countNote(note); }
  }
  if (doc.readyState === 'loading') { doc.addEventListener('DOMContentLoaded', init); } else { init(); }

  window.DOCart = {
    change: changeLine,
    refresh: refresh,
    openDrawer: function () { openDrawer(doc.querySelector('[data-do-drawer]')); },
    closeDrawer: function () { closeDrawer(doc.querySelector('[data-do-drawer]')); },
    saved: readSaved
  };
}());
